import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import WebcamModal from "./WebcamModal";
import FullPageLoader from "../FullPageLoader";
import { useSelector } from "react-redux";
import { getVerificationDetails } from "../utils/onboarding-utils/GetVerificationDetails";
import { submitDetails } from "../utils/onboarding-utils/SubmitDetails";
import { fileChange } from "../utils/onboarding-utils/FileChange";
import { fileUpload } from "../utils/onboarding-utils/FileUpload";
import VerificationError from "../VerificationError";

function Onboarding() {
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tid = params.get("tid");

  const [file, setFile] = useState("");
  const [areFieldsSet, setAreFieldsSet] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [isPictureTaken, setIsPictureTaken] = useState(false);
  const [verificationDetailsError, setVerificationDetailsError] =
    useState(null);
  const [isLoadingVerificationDetails, setIsLoadingVerificationDetails] =
    useState(true);
  const [progress, setProgress] = useState(0);
  const verificationData = useSelector((state) => state.nin.verificationData);

  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    gender: "",
    date_of_birth: "",
    photo_url: "",
  });

  const toggleShowModal = () => setShowModal((showModal) => !showModal);

  useEffect(() => {
    const reqController = new AbortController();
    getVerificationDetails(
      tid,
      setUserData,
      setIsLoadingVerificationDetails,
      setVerificationDetailsError
    );
    return () => reqController.abort();
  }, [tid]);

  useEffect(() => {
    Object.values(userData).includes("")
      ? setAreFieldsSet(false)
      : setAreFieldsSet(true);
  }, [userData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitDetails(userData.photo_url, tid, history);
  };

  const handleFileUpload = async (file) => {
    await fileUpload(
      file,
      userData,
      setUserData,
      setIsImageUploaded,
      setIsImageUploading,
      setProgress
    );
  };

  const handleChangeFile = async (b64String) => {
    fileChange(b64String, setFile, setIsPictureTaken, handleFileUpload);
  };

  if (isLoadingVerificationDetails) {
    return <FullPageLoader />;
  }

  if (verificationDetailsError) {
    return (
      <VerificationError
        type={verificationDetailsError ? "Verification" : "nin"}
      />
    );
  }

  return (
    <>
      <section className="section-border border-primary">
        <div className="container d-flex flex-column">
          <div className="row align-items-center justify-content-center gx-0 min-vh-100">
            <div className="col-12 col-md-10 col-lg-9 py-8 py-md-11">
              <h1 className="mb-0 fw-bold text-center text-primary">
                Onboarding
              </h1>
              <p className="mb-6 text-center text-muted">
                One last step, and you are done.🙂
              </p>
              <form className="mb-6" onSubmit={handleSubmit}>
                <div className="form-group row">
                  <div className="col-sm-4 mb-3 mb-sm-0">
                    <label className="form-label" htmlFor="first_name">
                      First Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-user"
                      name="first_name"
                      id="first_name"
                      defaultValue={userData.first_name}
                      readOnly
                    />
                  </div>
                  <div className="col-sm-4 mb-3 mb-sm-0">
                    <label className="form-label" htmlFor="last_name">
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-user"
                      name="last_name"
                      id="last_name"
                      defaultValue={userData.last_name}
                      readOnly
                    />
                  </div>
                  <div className="col-sm-4 mb-3 mb-sm-0">
                    <label className="form-label" htmlFor="middle_name">
                      Middle Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-user"
                      name="middle_name"
                      id="middle_name"
                      defaultValue={userData.middle_name}
                      readOnly
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <div className="col-sm-6 mb-3 mb-sm-0">
                    <label className="form-label" htmlFor="gender">
                      Gender
                    </label>
                    <input
                      className="form-control form-control-user"
                      name="gender"
                      id="gender"
                      defaultValue={userData.gender}
                      readOnly
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label" htmlFor="date_of_birth">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-user"
                      name="date_of_birth"
                      id="date_of_birth"
                      defaultValue={userData.date_of_birth}
                      readOnly
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <div className="col-sm-6 mb-3 mb-sm-0">
                    <label className="form-label" htmlFor="phone_number">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-user"
                      name="phone_number"
                      id="phone_number"
                      defaultValue={userData.phone_number}
                      readOnly
                    />
                  </div>
                  {userData.bvn || userData?.nin || verificationData.nin ? (
                    <div className="col-sm-6">
                      {userData?.nin || verificationData.nin ? (
                        <>
                          <label className="form-label" htmlFor="nin">
                            NIN
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-user"
                            name="nin"
                            id="nin"
                            defaultValue={userData.nin ?? verificationData.nin}
                            readOnly
                          />
                        </>
                      ) : (
                        <>
                          <label className="form-label" htmlFor="bvn">
                            BVN
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-user"
                            name="bvn"
                            id="bvn"
                            defaultValue={userData.bvn}
                            readOnly
                          />
                        </>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="form-group mt-8">
                  <p>Upload a photo of you (camera/webcam)</p>
                  {!isPictureTaken && (
                    <button
                      className="btn btn-outline-primary btn-sm w-100"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleShowModal();
                      }}
                    >
                      Take a picture
                    </button>
                  )}
                </div>
                {isImageUploaded ? (
                  <button
                    id="btn-upload"
                    className="btn btn-primary-soft btn-sm w-100 mb-4"
                    disabled
                  >
                    {" "}
                    Picture Uploaded
                  </button>
                ) : (
                  isPictureTaken && (
                    <>
                      {isImageUploading && (
                        <div
                          className="progress my-4"
                          style={{ height: "30px" }}
                        >
                          <div
                            className="progress-bar bg-primary"
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                            aria-valuenow={progress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          >
                            {progress && typeof progress === "number"
                              ? progress
                              : "0"}
                            %
                          </div>
                        </div>
                      )}
                    </>
                  )
                )}

                {areFieldsSet ? (
                  <button className="btn w-100 btn-primary" type="submit">
                    Next
                  </button>
                ) : (
                  <button
                    className="btn w-100 btn-primary"
                    type="button"
                    disabled={!areFieldsSet}
                  >
                    Next
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
      <WebcamModal
        showModal={showModal}
        toggleShowModal={toggleShowModal}
        handleChangeFile={handleChangeFile}
      />
    </>
  );
}
export default Onboarding;
